import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { orderTrackingModule } from '../../api/modules/orderTracking.module';
import { showToast } from '../../utils/toast';
import CreateLabelView from './create-label.view';

/**
 * /create-label, the container.
 *
 * The mutation key, the request shape (`{ order_id, is_delivered }`), the
 * response unwrapping (`payload.data?.data || payload.data`) and the tracking-id
 * fallback chain are unchanged. The waybill fetch still calls
 * `getWaybill(trackingId)` and still opens `response.url` in a new tab.
 *
 * A seller who left this page without the waybill must find it again rather than
 * buy a second parcel, so a successful response refreshes the order list (whose
 * row decides between "Confirm order" and "Download waybill") and is kept on this
 * history entry, where Back and reload still find it. The API refuses a second
 * booking regardless and answers `already_created`, which covers a stale tab.
 *
 * `isDelivered` is kept because it is part of the request body. Its checkbox
 * was already commented out in the page before this conversion, so the value is
 * always `false` today; it is preserved rather than removed, because removing
 * it would change the payload, and this conversion changes no payload.
 *
 * The `SellerDashboard/Layout` wrapper went; `MarketShell` is the real chrome.
 */
const CreateLabel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orderId = location?.state?.orderId || '';
  const [labelData, setLabelData] = useState(location?.state?.labelData || null);
  const [isDelivered] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const createLabelMutation = useMutation({
    mutationKey: ['create-label'],
    mutationFn: async (vars) => orderTrackingModule.createLabel(vars),
    onSuccess: (res) => {
      if (!res.success) {
        showToast.error(res.message || 'Failed to create label');
        setError(res.message || 'Failed to create label');
        return;
      }
      const payload = res.data || {};
      showToast.success(
        payload.already_created
          ? 'This order already has a label. You can download it below.'
          : 'Label created successfully',
      );
      setError(null);
      const shipment = payload.data?.data || payload.data || null;

      queryClient.invalidateQueries({ queryKey: ['seller-orders'] });

      if (isDelivered) {
        setLabelData(null);
      } else {
        setLabelData(shipment);
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, labelData: shipment },
        });
      }
    },
    onError: () => {
      showToast.error('Failed to create label');
      setError('Failed to create label');
    },
  });

  const handleGenerateLabel = async () => {
    if (!orderId) {
      showToast.error('Missing order id');
      setError('Missing order id');
      return;
    }
    createLabelMutation.mutate({
      order_id: orderId,
      is_delivered: isDelivered,
    });
  };

  const trackingId = useMemo(() => {
    if (!labelData) return '';
    return labelData.tracking_reference || labelData.parcels?.[0]?.tracking_reference || '';
  }, [labelData]);

  const handleDownload = () => {
    setDownloading(true);
    orderTrackingModule
      .getWaybill(trackingId)
      .then((response) => {
        if (response.success && response.url) {
          window.open(response.url, '_blank');
        } else {
          showToast.error(response.message || "Waybill isn't ready yet");
        }
      })
      .catch(() => showToast.error('Failed to download waybill'))
      .finally(() => setDownloading(false));
  };

  return (
    <CreateLabelView
      orderId={orderId}
      trackingId={trackingId}
      labelData={isDelivered ? null : labelData}
      isPending={createLabelMutation.isPending}
      error={error}
      onGenerate={handleGenerateLabel}
      onDownload={handleDownload}
      downloading={downloading}
    />
  );
};

export default CreateLabel;
